import React, { useEffect, useRef, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { Outlet, useLocation } from "react-router-dom";
import * as _ from "lodash";
import Loading from "./Loading";
import { useSelector, useDispatch } from "react-redux";
import { AppState, DEFAULT_APP_STATE } from "../../AppState/appState";
import { deploymentStatusApi } from "../../API/deploymentStatus";
import { PreviousPathContext } from "../../context/navigation/PreviousPathContext";
import { isTokenExpired, redirectToOktaLogin, getUserInfo } from "../../helpers";
import { validateOktaToken } from "../../API/validateOktaToken";
import { logUserIn, userPermissions } from "../../AppState/logUserIn";
import { ErrorMessages, STORAGE_KEYS } from "../../constants";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { PopoutNotification } from "../../AppState/popoutNotification";

export const RequiredAuth: React.FC = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: AppState) => state.isLoggedIn);
  const location = useLocation();
  const prevRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const [isValidatingToken, setIsValidatingToken] = useState(false);

  useEffect(() => {
    prevRef.current = location.pathname;
  }, [location.pathname]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // MAIN AUTH FLOW: Validate Okta token and fetch JWT after successful Okta login
  useEffect(() => {
    // Wait for authState to be ready
    if (!authState || authState.isPending) {
      return;
    }

    // Only proceed if user is authenticated in Okta
    if (!authState.isAuthenticated) {
      return;
    }

    // Skip if already logged in or currently validating
    if (isLoggedIn || isValidatingToken) {
      return;
    }

    // Check if JWT token already exists
    const existingJWT = getUserInfo()?.jwtToken;
    if (existingJWT) {
      // JWT exists, just set login state
      dispatch(logUserIn(true));
      return;
    }

    // Okta authenticated but no JWT - validate token to fetch JWT and permissions
    console.log("Okta authentication successful, validating token to fetch JWT...");
    setIsValidatingToken(true);

    validateOktaToken()
      .then((user) => {
        if (!isMountedRef.current) return;

        console.log("Token validation successful, JWT and permissions fetched");
        localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
        
        // Set user permissions
        dispatch(
          userPermissions(
            _.get(user, "permissions", DEFAULT_APP_STATE.userPermissions)
          )
        );
        
        // Set logged in state - this will allow RequiredAuth to render protected routes
        dispatch(logUserIn(true));
        setIsValidatingToken(false);
      })
      .catch((err) => {
        if (!isMountedRef.current) return;

        console.error("Token validation failed:", err);
        const message = err?.message ?? err;
        setIsValidatingToken(false);

        // Handle deployment errors silently (expected behavior)
        if (
          message === ErrorMessages.deploymentInprogressError ||
          message === ErrorMessages.statusCodeNotFound
        ) {
          console.warn("Deployment in progress or status check failed:", message);
          return;
        }

        // Show error notification for authentication failures
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.ERROR,
            message ??
              `Login via Okta was successful, but authentication with the server failed. Please log out of Okta and log in again to retry`,
            Date.now().toString()
          )
        );

        // Redirect to login on failure
        redirectToOktaLogin(oktaAuth, "Token validation failed");
      });
  }, [oktaAuth, authState, isLoggedIn, isValidatingToken, dispatch]);

  // Check JWT token expiration for already logged-in users
  useEffect(() => {
    // Wait for authState to be ready
    if (!authState || authState.isPending) {
      return;
    }

    // Only check token if user is authenticated in Okta AND logged in
    if (!authState.isAuthenticated || !isLoggedIn) {
      return;
    }

    try {
      const authUser = getUserInfo();
      const jwtToken = authUser?.jwtToken;
      
      // If no JWT token exists after login, this is an error state
      if (!jwtToken) {
        console.warn("JWT token missing despite being logged in, redirecting to login");
        redirectToOktaLogin(oktaAuth, "JWT token missing");
        return;
      }

      // Check if token is expired or about to expire
      const tokenStatus = isTokenExpired(jwtToken, 2);
      
      // Redirect if token is expired (true) or expired yesterday ("expiredYesterday")
      if (tokenStatus === true) {
        console.warn("JWT token expired or expiring soon, redirecting to login");
        redirectToOktaLogin(oktaAuth, "Token expired or expiring");
      } else if (tokenStatus === "expiredYesterday") {
        console.warn("JWT token expired yesterday, redirecting to login");
        redirectToOktaLogin(oktaAuth, "Token expired yesterday");
      }
    } catch (error) {
      console.error("Token expiration check failed:", error);
      // Don't redirect on error to avoid loops - let interceptor handle it
    }
  }, [oktaAuth, authState, isLoggedIn]);

  // Redirect if not authenticated in Okta
  useEffect(() => {
    // Wait for authState to be initialized
    if (!authState || authState.isPending) {
      return;
    }

    // If not authenticated in Okta, check deployment status then redirect
    if (!authState.isAuthenticated) {
      const checkDeploymentAndRedirect = async () => {
        try {
          const response = await deploymentStatusApi();
          if (response) {
            await redirectToOktaLogin(oktaAuth, "User not authenticated");
          }
        } catch (error) {
          // Log deployment check error but still proceed with authentication
          console.error("Deployment status check failed:", error);
          
          // Proceed with authentication even if deployment check fails
          try {
            await redirectToOktaLogin(oktaAuth, "User not authenticated (deployment check failed)");
          } catch (redirectError) {
            console.error("Error during authentication redirect:", redirectError);
          }
        }
      };

      checkDeploymentAndRedirect();
    }
  }, [oktaAuth, authState]);

  // Show loading if auth state is pending or not ready
  if (!authState || authState.isPending) {
    return <Loading />;
  }

  // Show loading if not authenticated, not logged in, or currently validating token
  if (!authState.isAuthenticated || !isLoggedIn || isValidatingToken) {
    return <Loading />;
  }

  // Render protected routes
  return (
    <PreviousPathContext.Provider value={prevRef.current}>
      <Outlet />
    </PreviousPathContext.Provider>
  );
};
